import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useState, type ElementType } from "react";
import { Mail, ArrowRight, Shield, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  extractCodeReference,
  fullNameFromEmail,
  isAdminLoggedIn,
  persistAdminSession,
  requestAdminOtp,
  verifyAdminOtp,
} from "@/lib/admin-login-api";

export const Route = createFileRoute("/admin/login")({
  // Already signed in? Skip the login screen.
  beforeLoad: () => {
    if (isAdminLoggedIn()) {
      throw redirect({ to: "/admin" });
    }
  },
  head: () => ({ meta: [{ title: "Admin Login — Jain Gaurav Yatra" }] }),
  component: AdminLogin,
});

/** Deep orange — matches admin CTA */
const ADMIN_LOGIN_PAGE_BG = "#F19969";

function AdminLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [codeReference, setCodeReference] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const resetToEmail = () => {
    setStep("email");
    setOtp("");
    setCodeReference(null);
  };

  const sendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) {
      toast.error("Please enter your admin email.");
      return;
    }
    setBusy(true);
    try {
      const data = await requestAdminOtp({
        email: addr,
        full_name: fullNameFromEmail(addr),
      });
      const ref = extractCodeReference(data);
      if (!ref) {
        toast.error("Email not found.");
        return;
      }
      setCodeReference(ref);
      setOtp("");
      setStep("otp");
      toast.success("Check your email for the OTP.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send OTP.");
    } finally {
      setBusy(false);
    }
  };

  const confirmOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const addr = email.trim();
    const code = otp.trim().replace(/\s/g, "");
    if (!codeReference || !addr) {
      toast.error("Session expired. Request a new OTP.");
      resetToEmail();
      return;
    }
    if (!code) {
      toast.error("Enter the OTP from your email.");
      return;
    }
    setBusy(true);
    try {
      const session = await verifyAdminOtp({
        email: addr,
        otp: code,
        code_reference: codeReference,
      });
      persistAdminSession({ email: addr, session });
      toast.success("Welcome, Admin");
      nav({ to: "/admin" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "OTP verification failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex min-h-[100dvh] flex-col items-center justify-center px-4 py-10 font-sans"
      style={{ backgroundColor: ADMIN_LOGIN_PAGE_BG }}
    >
      <div className="w-full max-w-[440px]">
        <Link
          to="/login"
          className="mb-5 inline-flex items-center gap-1.5 text-xs font-semibold text-white/90 transition hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5 shrink-0" /> Back to user login
        </Link>

        <div className="rounded-[1.35rem] border border-border bg-card p-8 shadow-[var(--shadow-soft)]">
          {step === "email" ? (
            <>
              <AdminLoginHeader
                title="Admin Portal"
                subtitle="Restricted Access"
                description="Enter your administrator email. We will send a one-time code to sign in."
              />
              <form onSubmit={sendOtp} className="mt-6 space-y-3.5">
                <LoginField icon={Mail} label="Admin Email">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@jaingauravyatra.org"
                    autoComplete="email"
                    disabled={busy}
                    className="w-full border-0 bg-transparent py-0.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none disabled:opacity-60"
                  />
                </LoginField>
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-primary mt-3 w-full rounded-xl py-3.5 text-sm font-bold disabled:pointer-events-none disabled:opacity-60"
                >
                  {busy ? "Sending…" : "Send OTP"} <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              </form>
            </>
          ) : (
            <>
              <AdminLoginHeader
                title="Verify OTP"
                subtitle="Restricted Access"
                description={`We sent a code to ${email.trim()}. Enter it below to continue.`}
              />
              <form onSubmit={confirmOtp} className="mt-6 space-y-3.5">
                <LoginField icon={KeyRound} label="One-time code">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    placeholder="Enter OTP"
                    disabled={busy}
                    className="w-full border-0 bg-transparent py-0.5 text-sm tracking-widest text-foreground placeholder:text-muted-foreground/60 outline-none disabled:opacity-60"
                  />
                </LoginField>
                <div className="flex flex-col gap-2 sm:flex-row-reverse sm:justify-between">
                  <button
                    type="submit"
                    disabled={busy}
                    className="btn-primary w-full rounded-xl py-3.5 text-sm font-bold sm:flex-1 disabled:pointer-events-none disabled:opacity-60"
                  >
                    {busy ? "Verifying…" : "Verify & sign in"}{" "}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={resetToEmail}
                    className="w-full rounded-xl border border-border bg-background py-3.5 text-sm font-semibold text-foreground shadow-sm transition hover:bg-muted/50 sm:w-auto sm:px-5 disabled:opacity-60"
                  >
                    Change email
                  </button>
                </div>
              </form>
            </>
          )}

          <div className="mt-6 rounded-xl border border-border bg-muted/40 px-3.5 py-3 text-[11px] leading-snug text-muted-foreground">
            <strong className="font-semibold text-foreground/90">Note:</strong> Admin actions are
            logged. Only authorized trustees should access this portal.
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-white/75">
          Protected by Jain Gaurav Yatra Trust · Role-based access
        </p>
      </div>
    </div>
  );
}

function AdminLoginHeader({
  title,
  subtitle,
  description,
}: {
  title: string;
  subtitle: string;
  description: string;
}) {
  return (
    <>
      <div className="mb-5 flex items-start gap-3.5">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-primary-foreground shadow-md"
          style={{ background: "var(--gradient-saffron)" }}
        >
          <Shield className="h-6 w-6" strokeWidth={2} />
        </div>
        <div className="min-w-0 pt-0.5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {subtitle}
          </div>
          <h1 className="font-serif text-2xl font-bold tracking-tight text-foreground md:text-[1.65rem]">
            {title}
          </h1>
        </div>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
    </>
  );
}

function LoginField({
  icon: Icon,
  label,
  children,
}: {
  icon: ElementType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="admin-login-field rounded-xl border border-input bg-background px-3.5 py-2.5 shadow-sm transition focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-ring/25">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3 shrink-0 text-primary" /> {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
